import Phaser from 'phaser';
import type { Input } from '../sim/sim';

/**
 * 1 本指の操作をシミュレーションへの Input に翻訳する。
 *
 * ドラッグとタップを取り違えないための切り分け:
 *   指を置いている間        → 常に横移動（指の位置へ泳ぐ）
 *   指を離したとき、移動量が小さく短時間だった → ダイブ
 *
 * Phase 0 で最初に検証すべき点がここ。「押しっぱなしでダイブ」は横移動と
 * 入力が衝突して成立しなかったため、ダイブを固定時間のコミット動作に変え、
 * タップに割り当てている（docs/01-game-design.md に反映済み）。
 */
const TAP_MOVE_LIMIT_PX = 18;
const TAP_TIME_LIMIT_MS = 260;
/** 指との距離がこの範囲に入ると横入力が緩む。m 単位。 */
const STEER_EASE_METERS = 2.5;

export class InputReader {
  private pointerDown = false;
  private pointerX = 0;
  private downX = 0;
  private downAt = 0;
  private moved = 0;
  /** 次の 1 フレームだけ true になるダイブのエッジ。 */
  private diveEdge = false;

  private keyLeft!: Phaser.Input.Keyboard.Key;
  private keyRight!: Phaser.Input.Keyboard.Key;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly pxPerMeter: number,
  ) {
    scene.input.on(Phaser.Input.Events.POINTER_DOWN, (p: Phaser.Input.Pointer) => {
      this.pointerDown = true;
      this.pointerX = p.x;
      this.downX = p.x;
      this.downAt = scene.time.now;
      this.moved = 0;
    });

    scene.input.on(Phaser.Input.Events.POINTER_MOVE, (p: Phaser.Input.Pointer) => {
      if (!this.pointerDown) return;
      this.pointerX = p.x;
      this.moved = Math.max(this.moved, Math.abs(p.x - this.downX));
    });

    const release = (p: Phaser.Input.Pointer) => {
      if (!this.pointerDown) return;
      this.pointerDown = false;
      const heldMs = scene.time.now - this.downAt;
      const travelled = Math.max(this.moved, Math.abs(p.x - this.downX));
      if (travelled < TAP_MOVE_LIMIT_PX && heldMs < TAP_TIME_LIMIT_MS) {
        this.diveEdge = true;
      }
    };
    scene.input.on(Phaser.Input.Events.POINTER_UP, release);
    scene.input.on(Phaser.Input.Events.POINTER_UP_OUTSIDE, release);

    const keyboard = scene.input.keyboard;
    if (keyboard) {
      this.keyLeft = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT);
      this.keyRight = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT);
      // 指のタップと同じ経路に載せる。ポーリングで走行中だけ読んでいたため、
      // タイトル画面で Space を押しても始まらなかった（ヒントには書いてあるのに）。
      keyboard.on('keydown-SPACE', () => {
        this.diveEdge = true;
      });
    }
  }

  /** そのフレームの入力を取り出す。エッジはここで消費される。 */
  read(swimmerX: number): Input {
    let lateral = 0;

    if (this.pointerDown) {
      const fingerMeters = this.pointerX / this.pxPerMeter;
      lateral = (fingerMeters - swimmerX) / STEER_EASE_METERS;
    }

    // キーボードは同時押し時に指の入力を上書きする（デスクトップでの調整用）。
    if (this.keyLeft?.isDown) lateral = -1;
    if (this.keyRight?.isDown) lateral = 1;

    const dive = this.diveEdge;
    this.diveEdge = false;

    return { lateral: Math.max(-1, Math.min(1, lateral)), dive };
  }

  /** タイトルと結果画面での「タップで進む」判定。 */
  consumeTap(): boolean {
    const tapped = this.diveEdge;
    this.diveEdge = false;
    return tapped;
  }

  reset(): void {
    this.pointerDown = false;
    this.diveEdge = false;
  }

  get steering(): boolean {
    return this.pointerDown;
  }

  get fingerX(): number {
    return this.pointerX;
  }

  /** スクリーンではなくシーン座標系のままで見せる。 */
  get scenePointer(): Phaser.Input.Pointer {
    return this.scene.input.activePointer;
  }
}
